import { ScanSearch, Sparkles } from 'lucide-react';
import { Button, Disclosure, Field, Select } from '@/components/ui';
import type { MatchingMode } from '@/types';

const STRATEGIES: ReadonlyArray<{ value: MatchingMode; label: string }> = [
  { value: 'auto', label: 'Automatic suggestion' },
  { value: 'exact', label: 'Exact point number' },
  { value: 'offset', label: 'Numeric offset' },
  { value: 'prefix', label: 'Numeric prefix or suffix' },
  { value: 'nearest', label: 'Nearest coordinates' },
];

const isMatchingMode = (value: string): value is MatchingMode =>
  STRATEGIES.some((strategy) => strategy.value === value);

/** Collapsible matching controls; proposals open for review before anything changes. */
export const MatchingStrategies = ({
  open,
  onOpenChange,
  matchingMode,
  detectedOffset,
  pairCount,
  unmatchedRecord,
  unmatchedField,
  onMatchingModeChange,
  onPreview,
  onSuggest,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchingMode: MatchingMode;
  detectedOffset: number | null;
  pairCount: number;
  unmatchedRecord: number;
  unmatchedField: number;
  onMatchingModeChange: (mode: MatchingMode) => void;
  onPreview: () => void;
  onSuggest: () => void;
}) => (
  <Disclosure
    id="matching-strategies"
    title="Matching strategies"
    meta={
      <>
        <span className="sm:hidden">
          {pairCount > 0 ? `${pairCount} pairs` : 'No pairs'}
        </span>
        <span className="hidden sm:inline">
          {pairCount > 0
            ? `${pairCount} pairs accepted · ${unmatchedRecord} Record, ${unmatchedField} Field unmatched`
            : `${unmatchedRecord} Record, ${unmatchedField} Field points`}
        </span>
      </>
    }
    open={open}
    onOpenChange={onOpenChange}
    headingLevel={2}
  >
    <div className="flex flex-wrap items-end gap-2">
      <Field label="Matching strategy" className="w-full sm:w-64">
        <Select
          value={matchingMode}
          onChange={(event) => {
            if (isMatchingMode(event.target.value))
              onMatchingModeChange(event.target.value);
          }}
        >
          {STRATEGIES.map((strategy) => (
            <option key={strategy.value} value={strategy.value}>
              {strategy.label}
            </option>
          ))}
        </Select>
      </Field>
      <Button leadingIcon={<ScanSearch />} onClick={onPreview}>
        Preview matches
      </Button>
      <Button variant="ghost" leadingIcon={<Sparkles />} onClick={onSuggest}>
        Suggest matches
      </Button>
    </div>
    <p className="mt-3 text-xs text-fg-muted">
      {detectedOffset !== null && matchingMode === 'offset'
        ? `Detected numeric offset: ${detectedOffset}. `
        : ''}
      Proposals open for review; accepted pairs and weights change only when you
      accept.
    </p>
  </Disclosure>
);
